<template>
    <AuthLayout>
        <div class="mt-8 w-full max-w-4xl px-4 sm:mt-12">
            <div class="mb-12 text-center">
                <h1 class="text-3xl font-bold text-gray-800 sm:text-4xl">
                    How will you use Topol?
                </h1>
                <p class="mt-[12px] text-gray-500">
                    Select the product that best fits your needs.
                </p>
            </div>
            <div class="grid gap-6 md:grid-cols-2">
                <!-- Plugin Card -->
                <div
                    class="group flex cursor-pointer flex-col overflow-hidden rounded-[8px] border-2 border-transparent bg-white shadow-card transition-all duration-200 hover:border-gray-200"
                    @click="selectType('plugin')"
                >
                    <div class="flex flex-1 flex-col p-6">
                        <div class="mb-4 flex justify-center">
                            <span
                                class="rounded-[16px] tracking-[-0.4px] bg-[linear-gradient(to_right,#FF9C2A,#FF6903)] px-4 py-2 text-2xl font-bold text-white"
                            >
                                Plugin
                            </span>
                        </div>

                        <h2
                            class="leading-[1.2] mb-4 text-center text-2xl font-extrabold text-gray-800 tracking-[-0.4px]"
                        >
                            Embed a drag&amp;drop email <br />editor in your app
                        </h2>

                        <p class="mb-5 text-center font-[400] tracking-[0px] text-sm text-gray-500">
                            White-label editor to integrate <br/>into platforms, CRMs, and SaaS products
                        </p>

                        <div
                            class="mb-6 flex items-center justify-center gap-2 text-xs text-gray-500"
                        >
                            <div class="flex items-center gap-2 bg-gray-100 rounded-full px-4 py-1">
                                <CodeBracketIcon class="h-3 w-3 stroke-[2.5] text-gray-500" />
                                <span>For teams embedding email editor in their platform</span>
                            </div>
                        </div>

                        <div class="mb-6 overflow-hidden rounded-[8px]">
                            <img
                                src="https://d1vyenbk43iobe.cloudfront.net/application/images/codeSnippet.png"
                                alt="Code snippet"
                                class="h-auto w-full object-cover"
                            />
                        </div>

                        <div class="mt-4 flex items-center justify-center">
                            <div
                                class="rounded-full bg-[linear-gradient(to_right,#FF9C2A,#FF6903)] px-24 py-4 text-center text-sm font-bold text-white transition-all duration-200 sm:bg-none sm:bg-[#777777] sm:group-hover:bg-[linear-gradient(to_right,#FF9C2A,#FF6903)]"
                            >
                                Start Plugin Trial
                            </div>
                        </div>
                    </div>
                </div>

                <!-- PRO Card -->
                <div
                    class="group flex cursor-pointer flex-col overflow-hidden rounded-[8px] border-2 border-transparent bg-white shadow-card transition-all duration-200 hover:border-gray-200"
                    @click="selectType('pro')"
                >
                    <div class="flex flex-1 flex-col p-6">
                        <div class="mb-4 flex justify-center">
                            <span
                                class="rounded-[16px] tracking-[-0.4px] bg-[linear-gradient(to_right,#40E0D0,#19BEAE)] px-4 py-2 text-2xl font-bold text-white"
                            >
                                PRO
                            </span>
                        </div>


                        <h2
                            class="leading-[1.2] mb-4 text-center text-2xl font-extrabold text-gray-800 tracking-[-0.4px]"
                        >
                            Create beautiful emails <br />together with your team
                        </h2>

                        <p class="mb-5 text-center font-[400] tracking-[0px] text-sm text-gray-500">
                            Cloud-based email &amp; landing page <br/>editor for individuals and teams
                        </p>

                        <div
                            class="mb-6 flex items-center justify-center gap-2 text-xs text-gray-500"
                        >
                            <div class="flex items-center gap-2 bg-gray-100 rounded-full px-4 py-1">
                                <UsersIcon class="h-3 w-3 text-gray-500" />
                                <span>Marketers, designers and small teams</span>
                            </div>
                        </div>

                        <div class="mb-6 overflow-hidden rounded-[8px]">
                            <img
                                src="https://d1vyenbk43iobe.cloudfront.net/application/images/editor.png"
                                alt="Email editor"
                                class="h-auto w-full object-cover"
                            />
                        </div>

                        <div class="mt-4 flex items-center justify-center">
                            <div
                                class="rounded-full bg-[linear-gradient(to_right,#40E0D0,#19BEAE)] px-24 py-4 text-center text-sm font-bold text-white transition-all duration-200 sm:bg-none sm:bg-[#777777] sm:group-hover:bg-[linear-gradient(to_right,#40E0D0,#19BEAE)]"
                            >
                                Start PRO Trial
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <p class="mt-6 text-center text-xs text-gray-500">
                Not sure which product to choose? You can switch between
                products anytime.
            </p>
        </div>
    </AuthLayout>
</template>

<script lang="ts" setup>
import { onBeforeUnmount, onMounted } from 'vue';
import { router } from '@inertiajs/vue3';
import { route } from 'ziggy-js';
import AuthLayout from './components/AuthLayout.vue';
import { UsersIcon } from '@heroicons/vue/24/solid';
import { CodeBracketIcon } from '@heroicons/vue/24/outline';
import { useGtm } from '@composables/useGtm';
import { capturePostHogEvent } from '@/plugins/posthog';

type AppType = 'plugin' | 'pro';

const { pushEvent } = useGtm();

const viewedAt = Date.now();
let lastClickedType: AppType | null = null;
let submissionResolved = false;

onMounted(() => {
    capturePostHogEvent('select_app_type_viewed');
});

onBeforeUnmount(() => {
    if (lastClickedType === null) {
        capturePostHogEvent('select_app_type_abandoned', {
            time_on_page_ms: Date.now() - viewedAt,
        });
    } else if (!submissionResolved) {
        capturePostHogEvent('select_app_type_left_during_submission', {
            app_type: lastClickedType,
            time_on_page_ms: Date.now() - viewedAt,
        });
    }
});

const selectType = (type: AppType) => {
    const previousClick = lastClickedType;
    lastClickedType = type;
    submissionResolved = false;

    capturePostHogEvent('select_app_type_box_clicked', {
        app_type: type,
        previous_click: previousClick,
        time_to_click_ms: Date.now() - viewedAt,
    });

    router.post(route('app-type.store'), { app_type: type }, {
        onSuccess: () => {
            submissionResolved = true;

            capturePostHogEvent('select_app_type_submitted', {
                app_type: type,
                time_to_submit_ms: Date.now() - viewedAt,
            });

            pushEvent('topol_select_app_type', {
                app_type: type,
            });

            if (type === 'plugin') {
                pushEvent('topol_plugin_trial_start');
            }
        },
        onError: (errors) => {
            submissionResolved = true;

            capturePostHogEvent('select_app_type_failed', {
                app_type: type,
                error_keys: Object.keys(errors),
            });
        },
    });
};
</script>

